/** SERVA — le hub « Plus ».
 *  Tout ce qui n'est pas quotidien vit ici, regroupé en quatre blocs.
 *  Sans ce hub, une navigation a 4 onglets laissait 15 ecrans inaccessibles. */
import React from "react";
import { Link } from "react-router-dom";
import {
  BadgeEuro, CalendarOff, ChevronRight, Clock3, Coins, Download, FileSpreadsheet,
  HelpCircle, Image, LineChart, Link2, LogOut, MessageSquare, Mic, Palette, PenLine,
  Scissors, Sparkles, User, Wallet,
} from "lucide-react";
import { EnTete, Micro } from "../components/Bits";

const BLOCS = [
  {
    titre: "Argent",
    items: [
      { to: "/finances", i: LineChart, l: "Pilotage", d: "Chiffre d’affaires, remplissage, paiements" },
      { to: "/finances#rentabilite", i: Coins, l: "Rentabilité", d: "Le €/heure réel de chaque prestation" },
      { to: "/finances#encaissements", i: Wallet, l: "Encaissements", d: "Journal, impayés, pourboires" },
      { to: "/reglages#export", i: FileSpreadsheet, l: "Exporter pour ma compta", d: "Tes clientes et tes rendez-vous, en tableur" },
    ],
  },
  {
    titre: "Ma vitrine",
    items: [
      { to: "/reglages#page", i: Link2, l: "Ma page publique", d: "Ton lien, tes couleurs, ta bio" },
      { to: "/story", i: Sparkles, l: "Kit Instagram", d: "Tes dispos et tes affiches, prêtes à poster" },
      { to: "/marque", i: Palette, l: "Brand Auto", d: "Ta palette et ta typo, tirées de tes photos" },
      { to: "/contenu", i: PenLine, l: "Contenu de la semaine", d: "5 publications déjà écrites, à copier" },
      { to: "/assistant", i: MessageSquare, l: "Assistant DM", d: "Les réponses Instagram déjà écrites" },
      { to: "/reglages#portfolio", i: Image, l: "Portfolio", d: "Les photos que voient tes clientes" },
    ],
  },  {
    titre: "Mon activité",
    items: [
      { to: "/voix", i: Mic, l: "La Voix", d: "Dicte tes changements d’agenda et ton journal du soir" },
      { to: "/reglages#prestations", i: Scissors, l: "Prestations & tarifs", d: "Durées, prix, temps de battement" },
      { to: "/reglages#horaires", i: Clock3, l: "Horaires", d: "Ta semaine, tes jours de repos" },
      { to: "/reglages#acomptes", i: BadgeEuro, l: "Acompte & encaissement", d: "Montant demandé, moyens de paiement acceptés" },
      { to: "/reglages#rappels", i: MessageSquare, l: "Rappels", d: "Modèles de messages, quota d'envoi" },
      { to: "/reglages#conges", i: CalendarOff, l: "Congés et jours fermés", d: "Fermer une date, ou une demi-journée" },
    ],
  },
  {
    titre: "Mon compte",
    items: [
      { to: "/reglages#profil", i: User, l: "Profil", d: "Nom d’activité, métier, téléphone" },
      { to: "/reglages#abonnement", i: BadgeEuro, l: "Abonnement", d: "19,90 €/mois ou 199 €/an, résiliation en un clic" },
      { to: "/reglages#export", i: Download, l: "Exporter mes données", d: "Tout, en un clic" },
      { to: "/aide", i: HelpCircle, l: "Aide & contact", d: "Questions fréquentes, assistance, urgences" },
    ],
  },
];

export default function Plus({ compte, surDeconnexion }) {
  return (
    <div className="space-y-8" data-testid="page-plus">
      <EnTete
        micro="Tout le reste"
        titre="Plus"
        sous={compte?.activite ? `${compte.activite}` : undefined}
      />

      {BLOCS.map((b) => (
        <section key={b.titre}>
          <Micro className="mb-3">{b.titre}</Micro>
          <div
            style={{
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-lg)",
              overflow: "hidden",
            }}
          >
            {b.items.map((it, idx) => {
              const I = it.i;
              return (
                <Link
                  key={it.l}
                  to={it.to}
                  data-testid={`plus-${it.l.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="flex items-center gap-3.5 px-4 py-3.5 cible-tactile entree"
                  style={{
                    borderTop: idx === 0 ? "none" : "1px solid var(--border)",
                    color: "var(--text)",
                  }}
                >
                  <span
                    className="inline-flex h-9 w-9 items-center justify-center shrink-0"
                    style={{
                      borderRadius: "var(--radius-md)",
                      background: "var(--accent-soft)",
                      color: "var(--accent)",
                    }}
                  >
                    <I size={17} strokeWidth={1.8} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[15px] font-semibold truncate">{it.l}</span>
                    <span
                      className="block text-[13px] truncate"
                      style={{ color: "var(--text-muted)" }}
                    >
                      {it.d}
                    </span>
                  </span>
                  <ChevronRight size={17} strokeWidth={1.8} style={{ color: "var(--text-subtle)" }} />
                </Link>
              );
            })}
          </div>
        </section>
      ))}

      <button
        onClick={surDeconnexion}
        data-testid="plus-deconnexion"
        className="w-full flex items-center justify-center gap-2 h-12 text-[15px] font-semibold cible-tactile"
        style={{
          borderRadius: "var(--radius-md)",
          border: "1px solid var(--border)",
          background: "var(--surface)",
          color: "var(--danger)",
        }}
      >
        <LogOut size={17} strokeWidth={1.8} />
        Se déconnecter
      </button>
    </div>
  );
}
